import React, { useState, useEffect } from 'react';
import '../styles/diagnostics/base.css';
import '../styles/diagnostics/components/pies.css';
import '../styles/diagnostics/components/legends.css';
import '../styles/diagnostics/components/interventions.css';
import '../styles/diagnostics/components/dependencies.css';
import '../styles/diagnostics/components/recommendations.css';
import '../styles/diagnostics/components/highlight-box.css';

/**
 * Test Harness for Leadership Burden Results (v7.0.0)
 *
 * This test component allows visual validation of:
 * - Zone classification logic (Foundational/Stretch/Mastery based on LPFF)
 * - Anxiety score impact on recovery calculations
 * - Zone-specific theming and messaging
 * - Dynamic CSS loading for zone styles
 * - Pie chart visualizations and legends
 * - Top 3 anxiety identification
 *
 * Test Controls:
 * - Zone switcher (Foundational/Stretch/Mastery)
 * - Persona selector (Mark/Sarah)
 * - Value at stake input
 * - Individual anxiety sliders (10 dimensions)
 * - Real-time LPFF calculation display
 */

function LeadershipBurdenResultsTest() {
  const [zone, setZone] = useState('stretch'); // Test zone: foundational, stretch, mastery
  const [persona] = useState('mark');
  const [valueAtStake] = useState(15.0);
  const [anxietyScores] = useState({
    anx1: 5, anx2: 6, anx3: 5, anx4: 7, anx5: 6,
    anx6: 5, anx7: 6, anx8: 5, anx9: 6, anx10: 7
  });

  // Anxiety definitions
  const anxietyDefinitions = [
    { id: 1, name: 'Imposter Complex', description: 'Fear of exposure or inadequacy', theme: 'psychological' },
    { id: 2, name: 'Fragile Authority', description: 'Questions about trust and respect', theme: 'psychological' },
    { id: 3, name: 'Over-Dependence', description: 'Fear of organizational collapse', theme: 'psychological' },
    { id: 4, name: 'Moral Injury', description: 'Compromise of values and integrity', theme: 'psychological' },
    { id: 5, name: 'Invisible Labour', description: 'Unrecognized emotional work', theme: 'relational' },
    { id: 6, name: 'Failing the Future', description: 'Concerns about wasted effort and legacy', theme: 'relational' },
    { id: 7, name: 'Team Disengagement', description: 'Learned helplessness and apathy', theme: 'relational' },
    { id: 8, name: 'Personal Depletion', description: 'Burnout and lack of energy', theme: 'existential' },
    { id: 9, name: 'Identity Drift', description: 'Losing sense of self', theme: 'existential' },
    { id: 10, name: 'Loss of Meaning', description: 'Questioning purpose and meaning', theme: 'existential' }
  ];

  // Calculate LPFF from anxiety scores
  const calculateLPFF = () => {
    const invertedTotal = Object.values(anxietyScores).reduce((sum, score) => {
      return sum + (10 - score); // Invert: lower anxiety score = higher problem frequency
    }, 0);
    return invertedTotal / 100; // Normalize to 0-1 scale
  };

  const lpff = calculateLPFF();

  // Persona baselines (DCF held constant for Leadership)
  const PERSONA_BASELINES = {
    mark: { name: 'Mark Ellison', role: 'Transformation Director', strainType: 'System Overload', dcf: 0.70 },
    sarah: { name: 'Sarah Lawson', role: 'Chief Operating Officer', strainType: 'Self Overload', dcf: 0.65 }
  };

  const personaData = PERSONA_BASELINES[persona];

  // Calculate all metrics
  const totalValue = valueAtStake;
  const totalLoss = totalValue * 0.43; // McKinsey: 43% average loss

  const crf = (personaData.dcf * 0.685) + (lpff * 0.315 * 1.08); // ACT coaching multiplier
  const recoverableValue = totalLoss * crf * 0.233; // Maximum 23.3% recovery
  const finalCaptureRate = 0.57 + (recoverableValue / totalValue);

  // Get top 3 anxieties (lowest scores = highest problems)
  const getTop3Anxieties = () => {
    const anxieties = anxietyDefinitions.map((anx) => ({
      ...anx,
      score: anxietyScores[`anx${anx.id}`] || 5
    }));
    return anxieties.sort((a, b) => a.score - b.score).slice(0, 3);
  };

  const top3Anxieties = getTop3Anxieties();

  // Zone-specific content
  const getZoneContent = () => {
    const stretchValueAvailable = totalValue * 0.067; // 6.7% stretch potential

    if (zone === 'foundational') {
      return {
        highlightLabel: 'Below Baseline Threshold',
        highlightValue: 'Foundational',
        highlightSubtext: 'Building essential psychological flexibility to access average transformation value',
        challengerQuestion1: `"Your psychological flexibility assessment places you below the baseline threshold. This means you're not yet positioned to access the 57% average value that most transformations achieve. If we could develop your psychological flexibility to that capability, what would this mean for your £${totalValue}M transformation?"`,
        challengerQuestion2: '"The dark green shows what baseline psychological flexibility looks like—the 57% average value most transformations achieve. The light blue? That\'s stretch potential once you\'ve built the foundation. First step: build foundational psychological flexibility to reach baseline. Are you ready?"',
        keyInsight: 'Building foundational psychological flexibility unlocks access to transformation value. Your anxiety patterns indicate you\'re below the baseline threshold needed to access the 57% average value. Once you reach baseline through ACT coaching, stretch interventions become effective and accessible value increases significantly.'
      };
    } else if (zone === 'stretch') {
      return {
        highlightLabel: 'Remaining Potential Value',
        highlightValue: `£${stretchValueAvailable.toFixed(2)}M`,
        highlightSubtext: 'With your solid psychological foundation you can stretch further, are you ready?',
        challengerQuestion1: `"If your transformation is worth £${totalValue}M, how much of that will you actually get?"`,
        challengerQuestion2: `"The dark green slice? That's your foundational psychological flexibility. The light green slice? That's your enhanced flexibility gains by working towards excellence. The last £${stretchValueAvailable.toFixed(2)}M is your stretch. Do you want it?"`,
        keyInsight: 'Your baseline psychological flexibility is strong. Advanced ACT interventions can now unlock additional value beyond the average transformation. Focus on optimizing your response to the highest-impact anxiety patterns to maximize your capture rate within the empirical ceiling.'
      };
    } else {
      return {
        highlightLabel: 'Remaining Potential Value',
        highlightValue: `£${(totalValue * 0.018).toFixed(2)}M`,
        highlightSubtext: 'Although you\'re currently in the top 10% you could stretch further, are you ready?',
        challengerQuestion1: `"If your transformation is worth £${totalValue}M, you're already capturing £${(totalValue * 0.652).toFixed(2)}M — 8 points above average. The question is: can you fine-tune your psychological flexibility to reach the ceiling?"`,
        challengerQuestion2: '"The dark green? That\'s your foundational psychological flexibility. The light green? That\'s your enhanced flexibility as a top performer. Your stretch is the final 1.8 points to reach peak performance. Excellence awaits you."',
        keyInsight: 'You\'re approaching the empirical ceiling for transformation value capture. The priority shifts to maintaining this exceptional psychological flexibility under pressure. Focus on resilience, sustainability, and preventing regression in your anxiety management patterns.'
      };
    }
  };

  const zoneContent = getZoneContent();

  // Value calculations
  const foundationalValue = totalValue * 0.57;
  const potentialStretch = totalValue * 0.10;
  const permanentlyLost = totalValue * 0.33;

  // Stretch zone specific calculations (4 segments)
  const stretchValueGained = totalValue * 0.033; // 3.3% extra value gained
  const stretchValueAvailable = totalValue * 0.067; // 6.7% stretch potential

  // Mastery zone specific calculations (4 segments)
  const masteryValueGained = totalValue * 0.082; // 8.2% extra value gained
  const masteryValueAvailable = totalValue * 0.018; // 1.8% stretch potential remaining

  // Load zone-specific CSS dynamically
  useEffect(() => {
    import(`../styles/diagnostics/zones/${zone}.css`).catch(err => {
      console.warn(`Could not load zone CSS for ${zone}:`, err);
    });
  }, [zone]);

  // Get score band class
  const getScoreBand = (score) => {
    if (score <= 2) return 'critical';
    if (score <= 4) return 'weak';
    if (score <= 6) return 'moderate';
    if (score <= 8) return 'strong';
    return 'excellent';
  };

  return (
    <div className={`zone-${zone}`}>
      {/* Test Controls - Fixed Top Right */}
      <div style={{
        position: 'fixed',
        top: '10px',
        right: '10px',
        background: 'white',
        padding: '20px',
        borderRadius: '8px',
        boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
        zIndex: 1000
      }}>
        <h4 style={{ marginTop: 0, marginBottom: '12px', fontSize: '14px', fontWeight: 'bold' }}>Test Controls</h4>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <button
            onClick={() => setZone('foundational')}
            style={{
              padding: '8px 16px',
              background: zone === 'foundational' ? '#dc2626' : '#16a34a',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '13px'
            }}
          >
            Foundational (LPFF &lt; 0.5)
          </button>

          <button
            onClick={() => setZone('stretch')}
            style={{
              padding: '8px 16px',
              background: zone === 'stretch' ? '#f59e0b' : '#16a34a',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '13px'
            }}
          >
            Stretch (0.5-0.65)
          </button>

          <button
            onClick={() => setZone('mastery')}
            style={{
              padding: '8px 16px',
              background: zone === 'mastery' ? '#10b981' : '#16a34a',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '13px'
            }}
          >
            Mastery (≥ 0.65)
          </button>
        </div>

        <p style={{ fontSize: '12px', marginTop: '12px', marginBottom: 0, color: '#6b7280' }}>
          LPFF: {lpff.toFixed(3)} | Capture: {(finalCaptureRate * 100).toFixed(1)}%
        </p>
      </div>

      <div className="container">
        {/* Header */}
        <div className="card">
          <div className="badge">STEP 4 OF 4</div>
          <h1>Leadership Anxiety Assessment Results</h1>
          <p className="subtitle">Leadership psychological flexibility analysis and transformation value recovery potential, for Jane Smith</p>

          {/* Persona Information */}
          <div className="persona-info">
            <p><strong>Selected Profile:</strong> {personaData.name} • {personaData.role}</p>
            <p><strong>Leadership Strain:</strong> {personaData.strainType}</p>
          </div>
        </div>

        {/* Value Analysis */}
        <div className="card">
          <h2>Value Analysis</h2>

          <div className="challenger-question">
            {zoneContent.challengerQuestion1}
          </div>

          <p style={{ marginBottom: '8px' }}>
            McKinsey studied thousands of transformations. Here's what's happening to your business case:
          </p>

          <div className="pie-section">
            <div className="pie-container">
              <div className="pie-chart pie-chart-full"></div>
              <div className="pie-center">
                <div className="pie-center-label">Total Value</div>
                <div className="pie-center-value">£{totalValue}M</div>
              </div>
            </div>

            <div className="legend">
              {zone === 'stretch' ? (
                <>
                  <div className="legend-item">
                    <div className="legend-color color-captured"></div>
                    <div className="legend-content">
                      <div className="legend-title">Foundational value realised</div>
                      <div className="legend-description">Your foundational psychological flexibility — the baseline</div>
                    </div>
                    <div className="legend-value">
                      <div className="legend-amount">£{foundationalValue.toFixed(2)}M</div>
                      <div className="legend-percent">57%</div>
                    </div>
                  </div>

                  <div className="legend-item">
                    <div className="legend-color color-recoverable"></div>
                    <div className="legend-content">
                      <div className="legend-title">Extra value you're gaining</div>
                      <div className="legend-description">Your enhanced flexibility gains — the dividends of solid psychological foundations</div>
                    </div>
                    <div className="legend-value">
                      <div className="legend-amount">£{stretchValueGained.toFixed(2)}M</div>
                      <div className="legend-percent">3.3%</div>
                    </div>
                  </div>

                  <div className="legend-item">
                    <div className="legend-color color-potential"></div>
                    <div className="legend-content">
                      <div className="legend-title">Your stretch</div>
                      <div className="legend-description">Further psychological flexibility gains available to you</div>
                    </div>
                    <div className="legend-value">
                      <div className="legend-amount">£{stretchValueAvailable.toFixed(2)}M</div>
                      <div className="legend-percent">6.7%</div>
                    </div>
                  </div>

                  <div className="legend-item">
                    <div className="legend-color color-lost"></div>
                    <div className="legend-content">
                      <div className="legend-title">Permanently lost</div>
                      <div className="legend-description">Transformation friction — can't be eliminated</div>
                    </div>
                    <div className="legend-value">
                      <div className="legend-amount">£{permanentlyLost.toFixed(2)}M</div>
                      <div className="legend-percent">33%</div>
                    </div>
                  </div>
                </>
              ) : zone === 'mastery' ? (
                <>
                  <div className="legend-item">
                    <div className="legend-color color-captured"></div>
                    <div className="legend-content">
                      <div className="legend-title">Foundational value realised</div>
                      <div className="legend-description">Your foundational psychological flexibility — the baseline</div>
                    </div>
                    <div className="legend-value">
                      <div className="legend-amount">£{foundationalValue.toFixed(2)}M</div>
                      <div className="legend-percent">57%</div>
                    </div>
                  </div>

                  <div className="legend-item">
                    <div className="legend-color color-recoverable"></div>
                    <div className="legend-content">
                      <div className="legend-title">Extra value you're gaining</div>
                      <div className="legend-description">Your enhanced flexibility gains — the dividends of being in the top 10% of performers</div>
                    </div>
                    <div className="legend-value">
                      <div className="legend-amount">£{masteryValueGained.toFixed(2)}M</div>
                      <div className="legend-percent">8.2%</div>
                    </div>
                  </div>

                  <div className="legend-item">
                    <div className="legend-color color-potential"></div>
                    <div className="legend-content">
                      <div className="legend-title">Your stretch</div>
                      <div className="legend-description">Further psychological flexibility gains available to you</div>
                    </div>
                    <div className="legend-value">
                      <div className="legend-amount">£{masteryValueAvailable.toFixed(2)}M</div>
                      <div className="legend-percent">1.8%</div>
                    </div>
                  </div>

                  <div className="legend-item">
                    <div className="legend-color color-lost"></div>
                    <div className="legend-content">
                      <div className="legend-title">Permanently lost</div>
                      <div className="legend-description">Transformation friction — can't be eliminated</div>
                    </div>
                    <div className="legend-value">
                      <div className="legend-amount">£{permanentlyLost.toFixed(2)}M</div>
                      <div className="legend-percent">33%</div>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="legend-item">
                    <div className="legend-color color-captured"></div>
                    <div className="legend-content">
                      <div className="legend-title">Foundational Value</div>
                      <div className="legend-description">Baseline psychological flexibility every transformation should achieve</div>
                    </div>
                    <div className="legend-value">
                      <div className="legend-amount">£{foundationalValue.toFixed(2)}M</div>
                      <div className="legend-percent">57%</div>
                    </div>
                  </div>

                  <div className="legend-item">
                    <div className="legend-color color-potential"></div>
                    <div className="legend-content">
                      <div className="legend-title">Potential Further Stretch</div>
                      <div className="legend-description">Additional value accessible once psychological baseline is secured</div>
                    </div>
                    <div className="legend-value">
                      <div className="legend-amount">£{potentialStretch.toFixed(2)}M</div>
                      <div className="legend-percent">10%</div>
                    </div>
                  </div>

                  <div className="legend-item">
                    <div className="legend-color color-lost"></div>
                    <div className="legend-content">
                      <div className="legend-title">Permanently Lost</div>
                      <div className="legend-description">Transformation friction — can't be eliminated</div>
                    </div>
                    <div className="legend-value">
                      <div className="legend-amount">£{permanentlyLost.toFixed(2)}M</div>
                      <div className="legend-percent">33%</div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          <p className="source">Source: McKinsey & Company, "Losing from day one", December 2021</p>
        </div>

        {/* Highlight: Zone */}
        <div className="highlight-box">
          <div className="highlight-label">{zoneContent.highlightLabel}</div>
          <div className="highlight-value">{zoneContent.highlightValue}</div>
          <div className="highlight-subtext">{zoneContent.highlightSubtext}</div>
        </div>

        {/* How Do You Capture It? */}
        <div className="card">
          <h2>How Do You Capture It?</h2>

          <div className="challenger-question">
            {zoneContent.challengerQuestion2}
          </div>

          <p style={{ marginBottom: '8px' }}>
            Value capture comes from two intervention channels. Here's the breakdown:
          </p>

          <div className="two-pie-section">
            {/* Left: The breakdown */}
            <div className="pie-panel">
              <div className="pie-panel-title">{zone === 'mastery' ? 'Optimization by Intervention Type' : 'Capture by Intervention Type'}</div>
              <div className="pie-panel-subtitle">Where does the £{zone === 'stretch' ? stretchValueAvailable.toFixed(2) : zone === 'mastery' ? masteryValueAvailable.toFixed(2) : (foundationalValue + potentialStretch).toFixed(2)}M come from?</div>

              <div className="pie-small-container">
                <div className="pie-small pie-chart-intervention">
                  <div className="pie-small-center">
                    <div className="pie-small-label">{zone === 'mastery' ? 'Remaining' : 'Potential'}</div>
                    <div className="pie-small-value">£{zone === 'stretch' ? stretchValueAvailable.toFixed(2) : zone === 'mastery' ? masteryValueAvailable.toFixed(2) : (foundationalValue + potentialStretch).toFixed(2)}M</div>
                  </div>
                </div>
              </div>

              <div className="legend-compact">
                <div className="legend-compact-item">
                  <div className="legend-compact-color color-system"></div>
                  <div className="legend-compact-content">
                    <div className="legend-compact-title">System Interventions</div>
                    <div className="legend-compact-desc">Organisational capability improvements</div>
                  </div>
                  <div className="legend-compact-value">
                    <div className="legend-compact-amount">£{zone === 'stretch' ? (stretchValueAvailable * 0.685).toFixed(2) : zone === 'mastery' ? (masteryValueAvailable * 0.685).toFixed(2) : ((foundationalValue + potentialStretch) * 0.685).toFixed(2)}M</div>
                    <div className="legend-compact-percent">68.5%</div>
                  </div>
                </div>
                <div className="legend-compact-item">
                  <div className="legend-compact-color color-leadership"></div>
                  <div className="legend-compact-content">
                    <div className="legend-compact-title">Leadership Interventions</div>
                    <div className="legend-compact-desc">Psychological flexibility coaching</div>
                  </div>
                  <div className="legend-compact-value">
                    <div className="legend-compact-amount">£{zone === 'stretch' ? (stretchValueAvailable * 0.315).toFixed(2) : zone === 'mastery' ? (masteryValueAvailable * 0.315).toFixed(2) : ((foundationalValue + potentialStretch) * 0.315).toFixed(2)}M</div>
                    <div className="legend-compact-percent">31.5%</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right: What this means */}
            <div className="pie-panel">
              <div className="pie-panel-title">What This Means</div>
              <div className="pie-panel-subtitle">Two levers, one goal: {zone === 'stretch' ? 'capture more value' : zone === 'mastery' ? 'optimization' : 'build baseline capability'}</div>

              <div style={{ padding: '16px 0' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
                  <div style={{ width: '40px', height: '40px', background: '#0EA5E9', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: '700' }}>68%</div>
                  <div>
                    <div style={{ fontWeight: '600', color: '#152C47' }}>System capability</div>
                    <div style={{ fontSize: '13px', color: '#697A90' }}>Fix the machine: dependencies, governance, delivery flow</div>
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
                  <div style={{ width: '40px', height: '40px', background: '#8B5CF6', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: '700' }}>32%</div>
                  <div>
                    <div style={{ fontWeight: '600', color: '#152C47' }}>Leadership flexibility</div>
                    <div style={{ fontSize: '13px', color: '#697A90' }}>Free the leadership: reduce psychological burden</div>
                  </div>
                </div>

                <div style={{ background: zone === 'stretch' ? '#fef3c7' : zone === 'mastery' ? '#d1fae5' : '#fee2e2', borderRadius: '8px', padding: '16px', marginTop: '16px' }}>
                  <div style={{ fontSize: '13px', color: zone === 'stretch' ? '#92400e' : zone === 'mastery' ? '#065f46' : '#991b1b' }}>
                    <strong>Key insight:</strong> {zone === 'stretch' ? 'Your performance is based on a solid foundation. The remaining potential comes from a stretch towards excellence by adding and embedding enhanced practices to compound this success.' : zone === 'mastery' ? 'Your performance is already well on the way to mastery. The remaining potential comes from fine-tuning and embedding the practices to further compound the success, rather than recovery.' : zoneContent.keyInsight}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Intervention Cards */}
          <div className="intervention-cards">
            <div className="intervention-card intervention-system">
              <div className="intervention-label">System Interventions</div>
              <div className="intervention-title">
                {zone === 'mastery' ? 'UB4 + DF System Optimization' : 'UB4 + DF System Intervention'}
              </div>
              <div className="intervention-value">£{zone === 'stretch' ? (stretchValueAvailable * 0.685).toFixed(2) : zone === 'mastery' ? (masteryValueAvailable * 0.685).toFixed(2) : ((foundationalValue + potentialStretch) * 0.685).toFixed(2)}M</div>
              <div className="intervention-desc">
                {zone === 'mastery'
                  ? 'The targetted outcome for fine-tuning organizational capability through advanced Uncertainty Management practices'
                  : zone === 'stretch'
                  ? 'The targetted outcome for organisational capability improvement through the Uncertainty Management framework'
                  : 'Build foundational organizational capability through strengthening the 14 dependency factors to reach baseline threshold'
                }
              </div>
              <ul className="intervention-items">
                {zone === 'mastery' ? (
                  <>
                    <li>14 dependency factors already strong</li>
                    <li>Incremental improvements to embed excellence</li>
                    <li>Fine-tuning governance and delivery practices</li>
                  </>
                ) : zone === 'stretch' ? (
                  <>
                    <li>14 dependency factors assessed</li>
                    <li>Hidden work surfaced and addressed</li>
                    <li>Governance and delivery optimised</li>
                  </>
                ) : (
                  <>
                    <li>Strategic clarity and value case quality</li>
                    <li>Delivery flow and interdependency management</li>
                    <li>Hidden work visibility and management</li>
                    <li>Portfolio governance and decision rights</li>
                    <li>Change implementation foundations</li>
                    <li>Stakeholder network health development</li>
                  </>
                )}
              </ul>
            </div>

            <div className="intervention-card intervention-leadership">
              <div className="intervention-label">Leadership Interventions</div>
              <div className="intervention-title">
                {zone === 'mastery' ? 'UB4 + DF Leadership Optimization' : 'UB4 + DF Leadership Intervention'}
              </div>
              <div className="intervention-value">£{zone === 'stretch' ? (stretchValueAvailable * 0.315).toFixed(2) : zone === 'mastery' ? (masteryValueAvailable * 0.315).toFixed(2) : ((foundationalValue + potentialStretch) * 0.315).toFixed(2)}M</div>
              <div className="intervention-desc">
                {zone === 'mastery'
                  ? 'The targetted outcome through advanced ACT-based executive coaching to sustain psychological flexibility at peak performance'
                  : zone === 'stretch'
                  ? 'The targetted outcome through ACT-based coaching to enhance psychological flexibility beyond baseline'
                  : 'Build foundational psychological flexibility through ACT-based coaching on the 10 anxiety patterns to reach baseline threshold'
                }
              </div>
              <ul className="intervention-items">
                {zone === 'mastery' ? (
                  <>
                    <li>10 anxiety patterns already well-managed</li>
                    <li>Advanced optimization coaching</li>
                    <li>Sustaining peak psychological flexibility</li>
                  </>
                ) : zone === 'stretch' ? (
                  <>
                    <li>Imposter complex and self-doubt</li>
                    <li>Fragile authority and respect anxiety</li>
                    <li>Over-dependence and key person risk</li>
                    <li>Moral injury and values compromise</li>
                    <li>Invisible labour and unrecognized work</li>
                    <li>Failing the future and legacy concerns</li>
                  </>
                ) : (
                  <>
                    <li>Loss of meaning and purpose clarity</li>
                    <li>Moral injury and values alignment</li>
                    <li>Identity drift and role boundaries</li>
                    <li>Performance anxiety and self-criticism</li>
                    <li>Workload overwhelm and capacity management</li>
                    <li>Stakeholder conflict and relationship stress</li>
                  </>
                )}
              </ul>
            </div>
          </div>
        </div>

        {/* Top 3 Anxieties */}
        <div className="card">
          <h2>Top 3 Leadership Anxieties Requiring Attention</h2>
          <p style={{ color: '#697A90', marginBottom: '16px' }}>
            These anxieties are experienced most frequently. <strong>Lower scores indicate higher frequency</strong> (0 = constantly, 10 = never).
          </p>

          <div className="dependency-list">
            {top3Anxieties.map((anx, index) => {
              // Get ACT intervention text based on anxiety type
              const getACTIntervention = (name) => {
                if (name === 'Loss of Meaning') {
                  return 'Values clarification work to reconnect with personal values and develop committed action aligned with what truly matters to you as a leader. Research shows 8% proven effectiveness (Skews et al. 2018) in improving acceptance and values-based action.';
                } else if (name === 'Moral Injury') {
                  return 'Acceptance and cognitive defusion to reduce struggle with difficult ethical situations. Learning to hold values-inconsistent experiences lightly while maintaining commitment to value-aligned action where possible.';
                } else if (name === 'Identity Drift') {
                  return 'Self-as-context work to develop a more flexible sense of identity. Contact with present moment to recognize you are more than your role, your thoughts, or your transformation outcomes.';
                } else if (name === 'Imposter Complex') {
                  return 'Cognitive defusion techniques to create distance from self-critical thoughts. Developing acceptance of uncertainty and psychological flexibility around performance anxiety.';
                } else if (name === 'Personal Depletion') {
                  return 'Present moment awareness practices and self-compassion work. Building capacity to notice depletion signals early and take values-aligned action toward sustainable performance.';
                } else if (name === 'Team Disengagement') {
                  return 'Values-based leadership work and committed action toward fostering psychological safety. Acceptance of team responses while maintaining commitment to engagement-building behaviors.';
                } else {
                  return 'ACT-based coaching to build psychological flexibility in this dimension through acceptance, defusion, values work, and committed action practices.';
                }
              };

              // Get question-format description
              const getQuestionDescription = (name) => {
                if (name === 'Loss of Meaning') {
                  return 'How often do you question whether your transformation work has any real purpose or meaning?';
                } else if (name === 'Moral Injury') {
                  return 'How often are you forced to compromise your values or make decisions that violate your sense of integrity?';
                } else if (name === 'Identity Drift') {
                  return 'How often do you feel like you\'re losing your sense of self in your leadership role?';
                } else if (name === 'Imposter Complex') {
                  return 'How often do you experience fear of being exposed as inadequate or not good enough?';
                } else if (name === 'Personal Depletion') {
                  return 'How often do you feel burned out, exhausted, or lacking the energy needed for your role?';
                } else if (name === 'Team Disengagement') {
                  return 'How often do you observe or worry about learned helplessness and apathy in your team?';
                } else {
                  return anx.description;
                }
              };

              return (
                <div key={anx.id} className={`dependency-item ${getScoreBand(anx.score)}`}>
                  <div className="dependency-header">
                    <div>
                      <div className="dependency-title">{index + 1}. {anx.name}</div>
                      <div className="dependency-description">{getQuestionDescription(anx.name)}</div>
                    </div>
                    <div className="dependency-score">{anx.score}/10</div>
                  </div>
                  <div className="dependency-impact">
                    <p><strong>ACT Intervention Focus:</strong> {getACTIntervention(anx.name)}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Recommended Next Steps */}
        <div className="card">
          <h2>Recommended Next Steps</h2>
          <p style={{ color: '#697A90', marginBottom: '16px' }}>
            <strong>{personaData.name}</strong>, based on your results, here are evidence-based interventions to {zone === 'foundational' ? 'build foundational psychological flexibility' : zone === 'stretch' ? 'enhance psychological flexibility beyond baseline' : 'sustain peak psychological flexibility'}:
          </p>

          <div className="recommendation-item">
            <h4>1. ACT-Based Leadership Coaching</h4>
            <p>Focus on your top 3 anxieties ({top3Anxieties[0]?.name}, {top3Anxieties[1]?.name}, {top3Anxieties[2]?.name}) with evidence-based psychological flexibility interventions.</p>
          </div>

          <div className="recommendation-item">
            <h4>2. Values Clarification Work</h4>
            <p>Your lowest-scoring anxieties center on values and meaning - targeted work here can significantly improve your leadership experience and transformation outcomes.</p>
          </div>

          <div className="recommendation-item">
            <h4>3. Monitor Progress</h4>
            <p>Re-assess every 3-6 months to track changes in your anxiety patterns and transformation capture rate.</p>
          </div>

          <div className="recommendation-item">
            <h4>4. Consider System Burden Assessment</h4>
            <p>Leadership interventions address 31.5% of transformation value loss. The remaining 68.5% is attributable to system-level factors - dependencies, governance, and delivery flow. Complete the System Burden Diagnostic to understand your organizational capability and identify opportunities to address the system-addressable portion of value at risk.</p>
          </div>
        </div>

        {/* CTA Section */}
        <div className="cta-section">
          <div className="cta-question">
            {zone === 'stretch' ? (
              <>
                £{stretchValueAvailable.toFixed(2)}M is sitting on the table, there for the taking.<br />
                Are you daring enough to stretch for it?
              </>
            ) : zone === 'mastery' ? (
              <>
                Ready to sustain peak psychological flexibility?<br />
                Let's discuss your mastery optimization plan
              </>
            ) : (
              <>
                Ready to build your psychological flexibility foundations?<br />
                Let's discuss your path to baseline capability
              </>
            )}
          </div>
          <div className="cta-subtext">
            {zone === 'stretch'
              ? 'Explore stretch interventions to maximize your transformation success'
              : zone === 'mastery'
              ? 'Book a Truth Session to explore mastery practices for sustained psychological flexibility'
              : 'Book a Truth Session to explore foundational psychological flexibility development'
            }
          </div>
          <p style={{ marginTop: '24px', marginBottom: '20px', opacity: '0.9' }}>
            {zone === 'stretch'
              ? "Book a Truth Session to discuss your results and explore the recommended next steps in detail. We'll help you identify the stretch interventions that will have the greatest impact on your transformation success."
              : zone === 'mastery'
              ? "Schedule a call to discuss your anxiety patterns and create a plan to sustain peak psychological flexibility. We'll help you understand how advanced ACT-based coaching can maintain your exceptional performance."
              : "Schedule a call to discuss your anxiety patterns and create a plan to build the foundational psychological flexibility that unlocks transformation value. We'll help you understand what reaching baseline threshold means for your leadership capability and your transformation."
            }
          </p>
          <button onClick={() => window.location.href = 'https://daringfutures.co.uk/book-truth-session'} className="btn-truth-session">
            Book Truth Session
          </button>
        </div>

        {/* Footer */}
        <div style={{ textAlign: 'center', color: '#9ca3af', fontSize: '14px', marginTop: '32px' }}>
          <p>Daring Futures • Leadership Burden Diagnostic • v7.0.0 • Step 4 of 4</p>
        </div>

        {/* Test Success Message */}
        <div style={{
          background: '#d1fae5',
          border: '2px solid #10b981',
          borderRadius: '8px',
          padding: '24px',
          marginTop: '32px'
        }}>
          <h3 style={{ color: '#065f46', marginBottom: '12px' }}>✓ Test Harness Active</h3>
          <p style={{ color: '#047857', fontSize: '14px', marginBottom: '8px' }}>
            This is the <strong>Leadership Burden Results Test Harness</strong>. You are viewing a non-database version with test controls.
          </p>
          <p style={{ color: '#047857', fontSize: '13px' }}>
            Use the fixed control panel (top-right) to test different zones, personas, values, and anxiety patterns. The component dynamically calculates LPFF, CRF, and displays zone-specific content. All calculations use the v2.0 algorithm with evidence-based constants.
          </p>
        </div>
      </div>
    </div>
  );
}

export default LeadershipBurdenResultsTest;
